import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Stack,
    TextField,
    Typography,
    Autocomplete,
    Chip
} from '@mui/material';
import axios from 'axios';
import Navbar from '../setup/Navbar';
import StickyHeadTable from '../../BuiltInComponent/TableWithPagination';
import '../../services/GlobalVariables';

const getTodayDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function KOTPrintLogReport() {
    const [selectedDate, setSelectedDate] = useState(getTodayDate());
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchLogData = async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (selectedDate) params.date = selectedDate;
            if (selectedStatus) params.status = selectedStatus;

            const response = await axios.get(`${global.SET_API_URL}kot/getPrintLog`, { params });
            if (response.data?.success) {
                setLogs(response.data.logs || []);
                const groupedStatuses = (response.data.statusOptions || [])
                    .map((entry) => entry?.printStatus)
                    .filter((value) => value !== undefined && value !== null && String(value).trim() !== '');
                setStatusOptions(groupedStatuses);
            } else {
                setLogs([]);
            }
        } catch (error) {
            console.error('Error fetching KOT print logs:', error);
            alert('Error loading KOT print log data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLogData();
    }, []);

    const tableRows = useMemo(() => {
        return (logs || []).map((row, index) => ({
            kotLogID: row.kotLogID || index + 1,
            srNo: index + 1,
            orderID: row.orderID,
            categoryCode: row.categoryCode,
            printerIP: row.printerIP,
            printStatus: row.printStatus,
            itemsPrinted: row.itemsPrinted,
            errorMessage: row.errorMessage || '-',
            printedAt: row.printedAt ? new Date(row.printedAt).toLocaleString() : '-'
        }));
    }, [logs]);

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                            KOT Print Log Report
                        </Typography>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Date"
                                    type="date"
                                    value={selectedDate}
                                    onChange={(event) => setSelectedDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} md={5}>
                                <Autocomplete
                                    options={statusOptions}
                                    value={selectedStatus}
                                    onChange={(_, newValue) => setSelectedStatus(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Print Status" placeholder="Select status" />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Stack direction="row" spacing={1} sx={{ height: '100%' }}>
                                    <Button variant="contained" fullWidth onClick={fetchLogData} disabled={isLoading}>
                                        {isLoading ? 'Loading...' : 'Apply Filters'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => {
                                            setSelectedDate(getTodayDate());
                                            setSelectedStatus(null);
                                            setTimeout(() => fetchLogData(), 0);
                                        }}
                                        disabled={isLoading}
                                    >
                                        Reset
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Chip label={`Rows: ${tableRows.length}`} size="small" color="primary" />
                        </Stack>

                        <StickyHeadTable
                            tableColumns={[
                                { fieldName: 'kotLogID', cName: 'ID', textAlign: 'left', width: 40, status: 'dis', dType: 'number' },
                                { fieldName: 'srNo', cName: '#', textAlign: 'left', width: 40, status: 'en', dType: 'number' },
                                { fieldName: 'orderID', cName: 'Order ID', textAlign: 'left', width: 90, status: 'en', dType: 'number' },
                                { fieldName: 'categoryCode', cName: 'Category', textAlign: 'left', width: 80, status: 'en', dType: 'text' },
                                { fieldName: 'printerIP', cName: 'Printer IP', textAlign: 'left', width: 140, status: 'en', dType: 'text' },
                                { fieldName: 'printStatus', cName: 'Print Status', textAlign: 'left', width: 110, status: 'en', dType: 'text' },
                                { fieldName: 'itemsPrinted', cName: 'Items', textAlign: 'right', width: 70, status: 'en', dType: 'number' },
                                { fieldName: 'errorMessage', cName: 'Error Message', textAlign: 'left', width: 220, status: 'en', dType: 'text' },
                                { fieldName: 'printedAt', cName: 'Printed At', textAlign: 'left', width: 170, status: 'en', dType: 'text' },
                                { fieldName: 'Edit', cName: 'Edit', textAlign: 'center', width: 60, status: 'dis', dType: 'text' },
                                { fieldName: 'Delete', cName: 'Delete', textAlign: 'center', width: 60, status: 'dis', dType: 'text' },
                                { fieldName: 'Extra', cName: 'Extra', textAlign: 'center', width: 60, status: 'dis', dType: 'text' }
                            ]}
                            tableData={tableRows}
                            perPage={25}
                        />
                    </CardContent>
                </Card>
            </Box>
        </>
    );
}
